import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPageTitle from '../components/AdminPageTitle';
import api from '../services/api';
import { getStorageUrl } from '../services/api';
import { fetchProducts } from '../services/products';
import { fetchAdminOrders } from '../services/orders';
import { fetchUsers } from '../services/users';
import './Dashboard.css';

const formatOrderDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatUserDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Signed up today';
  if (diffDays === 1) return 'Signed up yesterday';
  if (diffDays < 7) return `Signed up ${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsRes, productsRes, ordersRes, usersRes] = await Promise.all([
          api.get('/admin/dashboard/stats'),
          fetchProducts({ per_page: 5, page: 1 }),
          fetchAdminOrders({ per_page: 5, page: 1 }),
          fetchUsers({ per_page: 5, page: 1 }),
        ]);
        setStats(statsRes.data || {});
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
        setRecentOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
        setLatestUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } catch (err) {
        console.error('Dashboard load error:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load dashboard.');
        setStats({ products: 0, orders: 0, users: 0, earnings: 0 });
        setProducts([]);
        setRecentOrders([]);
        setLatestUsers([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getStatusClass = (status) => {
    if (!status) return 'status-default';
    const s = String(status).toLowerCase();
    const map = {
      active: 'status-active',
      inactive: 'status-inactive',
      pending: 'status-pending',
      processing: 'status-processing',
      purchased: 'status-purchased',
      shipped_out: 'status-shipped-out',
      in_nigeria: 'status-in-nigeria',
      completed: 'status-completed',
      refund: 'status-refund',
      cancelled: 'status-cancelled',
    };
    return map[s] || 'status-default';
  };

  const formatStatusLabel = (status) => {
    if (!status) return '—';
    const labels = {
      pending: 'Pending',
      processing: 'Processing',
      purchased: 'Purchased',
      shipped_out: 'Shipped Out',
      in_nigeria: 'In Nigeria',
      completed: 'Completed',
      refund: 'Refund',
      cancelled: 'Cancelled',
    };
    return labels[String(status).toLowerCase()] || status;
  };

  if (loading) {
    return (
      <div className="dashboard">
        <AdminPageTitle title="Dashboard" />
        <div className="dashboard-header">
          <h1>Welcome, Admin!</h1>
          <p>Loading dashboard...</p>
        </div>
        <div className="dashboard-loading">Loading...</div>
      </div>
    );
  }

  const statProducts = stats?.products ?? 0;
  const statOrders = stats?.orders ?? 0;
  const statUsers = stats?.users ?? 0;
  const statEarnings = stats?.earnings ?? 0;

  return (
    <div className="dashboard">
      <AdminPageTitle title="Dashboard" />
      <div className="dashboard-header">
        <h1>Welcome, Admin!</h1>
        <p>Here&apos;s the latest stats</p>
      </div>

      {error && (
        <div className="dashboard-error">{error}</div>
      )}

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card-green">
          <div className="stat-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>{statProducts} Products</h3>
          </div>
        </div>

        <div className="stat-card stat-card-yellow">
          <div className="stat-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          </div>
          <div className="stat-content">
            <h3>{statOrders} Orders</h3>
          </div>
        </div>

        <div className="stat-card stat-card-light-green">
          <div className="stat-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>{statUsers.toLocaleString()} Users</h3>
          </div>
        </div>

        <div className="stat-card stat-card-light-green">
          <div className="stat-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>¥{Number(statEarnings).toLocaleString()} Earnings</h3>
            <p>This Month</p>
          </div>
        </div>
      </div>

      {/* Products Management - Recent 5 */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>Products Management</h2>
          <button
            className="add-product-btn"
            onClick={() => navigate('/admin/products/new')}
          >
            + Add Product
          </button>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Product</th>
                <th>Stock</th>
                <th>Price</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No products yet</td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr
                    key={product.id}
                    onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{product.id}</td>
                    <td>
                      <div className="product-cell">
                        {product.image ? (
                          <img src={getStorageUrl(product.image)} alt={product.name} />
                        ) : (
                          <span className="product-no-img">—</span>
                        )}
                        <span>{product.name}</span>
                      </div>
                    </td>
                    <td>{product.stock != null ? product.stock : '—'}</td>
                    <td>¥{Number(product.price || 0).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className={`status-btn ${getStatusClass(product.status)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/products/${product.id}/edit`);
                        }}
                      >
                        {(product.status || 'active').charAt(0).toUpperCase() + (product.status || 'active').slice(1)}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Orders (5) and Latest Users (5) */}
      <div className="dashboard-grid">
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Orders</h2>
            <button
              type="button"
              className="view-all-btn"
              onClick={() => navigate('/admin/orders')}
            >
              View All ↑
            </button>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data">No orders yet</td>
                  </tr>
                ) : (
                  recentOrders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => navigate('/admin/orders')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{order.order_number || order.id}</td>
                      <td>
                        <div className="customer-cell">
                          <div>
                            <div className="customer-name">{order.user?.name || order.user?.email || 'N/A'}</div>
                            <div className="customer-details">{order.user?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>¥{Number(order.total || 0).toLocaleString()}</td>
                      <td>
                        <span className={`status-btn ${getStatusClass(order.status)}`}>
                          {formatStatusLabel(order.status)}
                        </span>
                      </td>
                      <td>{formatOrderDate(order.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h2>Latest Users</h2>
            <button
              type="button"
              className="view-all-btn"
              onClick={() => navigate('/admin/users')}
            >
              View All ↑
            </button>
          </div>
          <div className="users-list">
            {latestUsers.length === 0 ? (
              <p className="no-data">No users yet</p>
            ) : (
              latestUsers.map((user) => (
                <div key={user.id} className="user-item">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email || 'U')}&background=1a5d1a&color=fff`}
                    alt=""
                    className="user-avatar"
                  />
                  <div className="user-info">
                    <div className="user-name">{user.name || user.email || 'N/A'}</div>
                    <div className="user-date">{formatUserDate(user.created_at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
